import React, { useCallback, useMemo } from 'react';
import { Chart } from 'react-google-charts';
import styled from 'styled-components';
import redX from '../../assets/images/red-x.svg';
import greenCheck from '../../assets/images/green-check.svg';
import { LinkDD } from '../Stories/Links';

// Scrolling on all tables if they are too long
const TableChart = styled.div`
  max-width: ${(props) =>
    props.$tableType === 'geo_concepts_table' ? '50rem' : '100%'};
  .google-visualization-table {
    width: 100%;

    & > div {
      scrollbar-width: thin;
    }

    & > div:first-child {
      max-height: 428px !important;
      overflow-y: auto !important;
    }

    &-table {
      width: 100%;
      font-size: 0.875rem;

      td {
        padding: 0.5rem;

        a {
          color: #000000;
          font-size: 0.875rem;
          font-weight: 700;

          &:hover,
          &:focus {
            text-decoration: none;
          }
        }
      }
    }

    &-tr {
      &-even {
        background: #fafafa;

        td {
          border: 0.5rem solid #fafafa;
        }
      }

      &-odd {
        background: #ffffff;

        td {
          border: 0.5rem solid #ffffff;
        }
      }
    }

    &-tr-head {
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1.2;

      .google-visualization-table-th {
        background: #f3f9fa;
        padding: 1rem;
        text-align: left;

        &.unsorted::after {
          content: url('data:image/svg+xml;utf8,<svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.2" d="M0.756964 7.54496H8.24304C8.91615 7.54496 9.25271 8.39073 8.77775 8.88382L5.03472 12.7698C4.73905 13.0767 4.26095 13.0767 3.96843 12.7698L0.222245 8.88382C-0.252712 8.39073 0.0838466 7.54496 0.756964 7.54496ZM8.77775 4.11618L5.03472 0.230219C4.73905 -0.0767395 4.26095 -0.0767395 3.96843 0.230219L0.222245 4.11618C-0.252712 4.60927 0.0838466 5.45504 0.756964 5.45504H8.24304C8.91615 5.45504 9.25271 4.60927 8.77775 4.11618Z" fill="black"/></svg>');
          padding-left: 0.375rem;
          vertical-align: middle;
        }
      }
    }
  }

  .vote-icon {
    display: flex;
    align-items: center;
    gap: 0.25rem;

    span {
      font-size: 0.75rem;
      height: 0.85rem;
      font-weight: 700;
      text-transform: uppercase;
    }
  }
`;

const DownloadWrapper = styled.div`
  margin-block-start: 1rem;
`;

const DownloadButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #0a819f;
  color: #ffffff;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
`;

const stripHtmlTags = (str) => {
  if (!str) return '';
  return str.replace(/<\/?[^>]+(>|$)/g, '');
};

const convertToCSV = (data, id) => {
  if (!data?.length) return '';

  let processedData = [...data];

  if (id === 'latest_bill_support_opposition') {
    // Process only the data rows, keep headers intact
    processedData = [
      data[0],
      ...data.slice(1).map((row) =>
        row.map((cell, index) =>
          // Assuming organization name is in first column
          index === 0 ? stripHtmlTags(cell) : cell
        )
      ),
    ];
  }

  if (id === 'witnesses') {
    processedData = [
      data[0].map((cell) =>
        typeof cell === 'object' && cell !== null ? cell.v : cell
      ),
      ...data
        .slice(1, -1)
        .map((row) =>
          row.map((cell, index) =>
            typeof cell === 'object' && cell !== null
              ? index === 0 || index === row.length - 1
                ? cell.v
                : cell.f
              : cell
          )
        ),
    ];
  }

  const headers = processedData[0].map((col) => `"${col}"`).join(',');
  const rows = processedData
    .slice(1)
    .map((row) => row.map((cell) => `"${cell}"`).join(','));

  return `${headers}\n${rows.join('\n')}`;
};

const convertVoteHistoryData = (data) => {
  const headers = [
    'Location',
    'Vote Date',
    'Motion',
    'Ayes',
    'Noes',
    'NVRs',
    'Outcome',
  ];

  return [
    headers,
    ...data.map((item) => [
      item.name,
      new Date(item.vote_date).toLocaleDateString('en-US'),
      item.motion_text,
      Number(item.ayes),
      Number(item.noes),
      Number(item.nvrs),
      item.outcome,
    ]),
  ];
};

const convertDataOrgPosition = (data) => {
  const headers = [
    'Organization',
    'Position',
    'Date',
    'Source',
    'Contrib. to Legislators',
    'Contrib. to Governor',
  ];

  return [
    headers,
    ...data.map((item) => [
      LinkDD(item.name, item.oc_id, 'org', null, true),
      item.alignment === 'For'
        ? `<div class="vote-icon" style="color: #386F1F;"><span>For</span><img src=${greenCheck} alt="Green Check Icon"/></div>`
        : `<div class="vote-icon" style="color: #BE2C25;"><span>Against</span><img src=${redX} alt="Red X Icon"/></div>`,
      item.alignment_date,
      'Bill Analysis',
      item.total_donated_current_legislators === 'NO RECORD'
        ? 'NO RECORD'
        : `$${parseFloat(
            item.total_donated_current_legislators
          ).toLocaleString()} [${item.leg_time_period}]`,
      item.total_donated_to_gov === 'NO RECORD'
        ? 'NO RECORD'
        : `$${parseFloat(item.total_donated_to_gov).toLocaleString()} [${
            item.time_period_gov
          }]`,
    ]),
  ];
};

const convertGeographicData = (data) => {
  const headers = ['Region', 'Term', 'Found in', 'Points'];

  return [
    headers,
    ...data.map((item) => [
      item.Region,
      item.Term,
      item['Found in'],
      parseInt(String(item.Points), 10),
    ]),
  ];
};

const convertWitnessesData = (data) => {
  // TODO: Remove FormData() and Personas and transform the data here
  const headers = [
    'Speaker',
    'Role',
    'Affiliations',
    'Position',
    'Time',
    'Link to Transcript',
  ];

  return [headers, ...data];
};

const DEFAULT_OPTIONS = {
  allowHtml: true,
  cssClassNames: { tableCell: 'cell', width: 'width' },
  showRowNumber: false,
};

const ChartTable = ({ dataTable, options, id, billName }) => {
  const convertedData = useMemo(() => {
    if (!dataTable?.length) return null;

    switch (id) {
      case 'latest_bill_support_opposition':
        return convertDataOrgPosition(dataTable);
      case 'bill_vote_history':
        return convertVoteHistoryData(dataTable);
      case 'geo_concepts_table':
        return convertGeographicData(dataTable);
      case 'witnesses':
        return convertWitnessesData(dataTable);
      default:
        return dataTable;
    }
  }, [dataTable, id]);

  const handleDownload = useCallback(() => {
    if (!convertedData) return;

    const csv = convertToCSV(convertedData, id);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${billName} ${id
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [billName, convertedData, id]);

  // overwrites with any option passed in
  const finalOptions = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...options,
    }),
    [options]
  );

  if (!convertedData) {
    return null;
  }

  return (
    <TableChart $tableType={id}>
      <Chart
        width="100%"
        chartType="Table"
        loader={<div>Loading Chart</div>}
        data={convertedData}
        options={finalOptions}
      />
      <DownloadWrapper>
        <DownloadButton onClick={handleDownload}>
          Download Table Data
        </DownloadButton>
      </DownloadWrapper>
    </TableChart>
  );
};
export default ChartTable;
